<template>

  <v-container fluid class="pb-4 delivery-order-manager" v-if="route">

    <v-toolbar flat height="64" style="border-radius:4px;" class="app-fixed-toolbar order-manager-toolbar">
      <div class="title">{{ route.name }}</div>
      <v-spacer />
      <v-btn text icon color="primary" @click="closeOrderManager">
        <v-icon large>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <div class="main-content px-10 mt-2">
      <v-container class="order-group-container">
        <v-row>
          <v-col cols="12" xs="12" sm="6" md="6" lg="6" class="d-flex align-center justify-center justify-sm-start">
            <div class="route-info-text">
              Delivery for: <span style="color:#c9c9c9;font-weight:500" class="ml-1">{{ formatDate(route.scheduled_start_time) }}</span>
            </div>
          </v-col>
          <v-col cols="12" xs="12" sm="6" md="6" lg="6" class="d-flex align-center justify-center justify-sm-end">
            <div class="route-info-text">
              Total orders: <span style="color:#c9c9c9;font-size:1.4rem;font-weight:500" class="ml-1">{{ orders.length }}</span>
            </div>
          </v-col>
        </v-row>
        <v-row class="mt-2">
          <v-col cols="12" class="d-flex align-center justify-center justify-sm-start">
            <v-icon small color="grey darken-1" style="padding-bottom:1px;margin-right:4px;">mdi-directions-fork</v-icon>
            <router-link class="item-link" :to="{ name: 'route-info-overview', params: { routeId: route.id } }">
              <span style="font-size:1.0rem;">View Route Info</span>
            </router-link>
          </v-col>
        </v-row>
      </v-container>

      <!---------------------------------------->
      <!-- Delivered / Rescheduled / Canceled -->
      <!---------------------------------------->
      <v-container class="order-group-container mt-4">
        <v-row>
          <v-col>
            <div class="title">Delivered / Rescheduled ({{ ordersCompleted.length }})</div>
          </v-col>
        </v-row>
        <v-row v-if="ordersCompleted.length === 0">
          <v-col class="d-flex align-center justify-center">
            <div style="height:24px;" class="list-empty-text"></div>
          </v-col>
        </v-row>
        <template v-else>
          <route-order-action-card
              v-for="order in ordersCompleted"
              :key="`completed-${order.order_number}`"
              :order="order"
              :hideActions="true" />
        </template>
      </v-container>

      <!----------------------------------------->
      <!-- Remaining Deliveries (with actions) -->
      <!----------------------------------------->
      <v-container class="order-group-container">
        <!-- Title & Actions -->
        <v-row>
          <v-col cols="12" xs="12" sm="3" md="4" lg="3">
            <div class="title">Remaining ({{ ordersToDeliver.length }})</div>
          </v-col>
          <v-col cols="12" xs="12" sm="9" md="8" lg="9" class="d-flex align-center justify-xs-center justify-sm-end">
            <template v-if="allowReordering">
              <!-- Loading graphic -->
              <div style="width:175px;" class="d-flex align-center" v-if="isReordering || isSendingNotifications">
                <v-progress-circular indeterminate :size="24" color="primary" />
                <div class="ml-3 updating-text" v-if="isReordering">Updating route...</div>
                <div class="ml-3 updating-text" v-if="isSendingNotifications">Sending emails...</div>
              </div>

              <!-- Re-ordering Error -->
              <div class="d-flex align-center" v-else-if="reorderingError">
                <v-icon small color="error" class="mr-2">mdi-alert</v-icon>
                <div class="mr-4 reordering-error-text">{{ reorderingError }}</div>
                <v-btn small color="grey darken-3" @click="clearReorderingError">Ok</v-btn>
              </div>

              <!-- Re-ordering Success -->
              <div style="width:196px;" v-else-if="reorderingSuccess">
                <div class="d-flex align-center">
                  <v-icon color="green" style="font-size:18px;" class="mr-1">mdi-check-circle</v-icon>
                  <div class="mr-4 reordering-success-text">Route Updated</div>
                  <v-btn v-if="!enableSendNotificationsOnChange" small color="grey darken-2" @click="clearReorderingSuccess">Ok</v-btn>
                </div>
                <!-- <div class="ml-1 mt-2 d-flex align-center">
                  <div class="mr-4 reorder-send-message-text">Send schedule update emails to recipients?</div>
                  <v-btn small color="grey darken-2" class="mr-3" @click="sendOrderUpdateNotifications">Yes</v-btn>
                  <v-btn small color="grey darken-2" @click="clearReorderingSuccess">No</v-btn>
                </div> -->
              </div>

              <!-- Sending Notifications Error -->
              <div class="d-flex align-center" v-else-if="sendingNotificationsError">
                <v-icon small color="error" class="mr-2">mdi-alert</v-icon>
                <div class="mr-4 reordering-error-text">{{ sendingNotificationsError }}</div>
                <v-btn small @click="clearSendingNotificationsError">Close</v-btn>
              </div>

              <!-- Sending Notifications Success -->
              <div v-else-if="sendingNotificationsSuccessMessage">
                <div class="d-flex align-center">
                  <v-icon color="green" style="font-size:18px;" class="mr-1">mdi-check-circle</v-icon>
                  <div class="mr-4 reordering-success-text">{{ sendingNotificationsSuccessMessage }}</div>
                </div>
              </div>

              <!-- Re-order Actions -->
              <div class="d-flex align-center" v-else-if="isReorderMode">
                <v-checkbox
                    v-if="haveDeliveriesChanged"
                    class="ma-0 mr-4"
                    hide-details
                    label="Send schedule update emails?"
                    v-model="enableSendNotificationsOnChange" />
                <v-btn
                    color="primary"
                    :disabled="!haveDeliveriesChanged"
                    @click="validateSaveReorderChanges">
                  Update Route
                </v-btn>
                <v-btn
                    icon
                    color="red"
                    class="ml-4"
                    @click="cancelReorderChanges">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
              <template v-else-if="ordersThatCanMove.length > 1">
                <v-btn
                    plain
                    color="grey lighten-3"
                    @click="enableReorderMode">
                  <v-icon left>mdi-sort</v-icon>
                  Re-order Deliveries
                </v-btn>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="#9c9c9c" v-bind="attrs" v-on="on" style="cursor:pointer;font-size:21px">
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <div>Pickups cannot be changed.</div>
                </v-tooltip>
              </template>
            </template>
          </v-col>
        </v-row>

        <!-- Order List (Re-order Mode) -->
        <draggable v-model="ordersToDeliver" @start="handleDragStart" @end="handleMove" draggable=".item-sortable" v-if="isReorderMode">
          <transition-group>
            <route-order-action-card
                v-for="(order, index) in ordersToDeliver"
                :key="`delivering-${index}`"
                :class="(canMoveInReorder(order)) ? 'item-sortable' : 'item-blocked'"
                :style="(canMoveInReorder(order)) ? 'cursor:grab;' : 'cursor:not-allowed;'"
                :order="order"
                :orderIndex="index+1"
                :isReorderMode="isReorderMode"
                :canMove="canMoveInReorder(order)"
                :canRescheduleInTransit="true"
                :isUpdating="dataUpdating[order.id] === true" />
          </transition-group>
        </draggable>

        <!-- Order List (Default Mode) -->
        <route-order-action-card
            v-else
            v-for="(order, index) in ordersToDeliver"
            :key="`delivering-${index}`"
            :order="order"
            :orderIndex="index+1"
            :requiresRouteChange="requiresRouteChangeForReschedule(order)"
            :isReorderMode="false"
            :canRescheduleInTransit="true"
            :isUpdating="dataUpdating[order.id] === true"
            :updatingError="dataUpdatingError[order.id]"
            @rescheduleOrder="rescheduleOrder"
            @cancelOrder="cancelOrder" />
      </v-container>
    </div>

    <!---------------------------------------------------------------->
    <!-- Client Coords & Route Change Info (for re-order scenarios) -->
    <!---------------------------------------------------------------->
    <v-dialog persistent :fullscreen="isMobileScreen" max-width="580" v-model="isRouteChangeDialogOpen" v-if="ordersWithRouteChange.length > 0">
      <v-card max-width="580px" class="py-0">
        <v-card-title>
          Route Change Required
        </v-card-title>
        <v-card-text class="client-coords-dialog-text">
          <!-- Client geolocation -->
          <div class="mt-4 px-2 py-3 client-coords-container">
            <client-geo-location
                ref="client-geo-location"
                mainMessage="Your route is changing due to the re-order."
                :enableTrueReRoute="enableTrueReRoute"
                @canceled="closeRouteChangeDialog()" />
          </div>
          <!-- Route change details -->
          <template v-if="!enableTrueReRoute">
            <div class="mt-8">Describe the change to your route:</div>
            <div style="max-width:580px;" class="mt-4">
              <v-textarea
                  rows="1"
                  outlined
                  required
                  auto-grow
                  :rules="$root.rules.fieldRequired"
                  hide-details="auto"
                  v-model.trim="notesRouteChange" />
            </div>
          </template>
        </v-card-text>
        <v-card-actions class="py-4">
          <v-btn
              large
              plain
              color="primary"
              class="ml-2 px-5"
              @click="closeRouteChangeDialog(true)">
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
              large
              color="primary"
              class="mr-2 px-5"
              :disabled="!canSaveRouteChanges"
              @click="handleReRouteAndSave">
            Make Route Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>

</template>

<script>
import Draggable from 'vuedraggable'
import RouteOrderActionCard from '@/components/routes/RouteOrderActionCard'
import ClientGeoLocation from '@/components/app/ClientGeoLocation'

export default {
  name: 'DeliveryOrderManager',

  components: {
    Draggable,
    RouteOrderActionCard,
    ClientGeoLocation
  },

  props: {
    route: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      enableTrueReRoute: false, // experimental feature (not ready)
      ordersCompleted: [],
      ordersToDeliver: [],
      ordersThatCanMove: [],
      activeDeliveryStepIndex: -1,
      activeOrdersToDeliver: [], // used as backup for cancel action
      previousRouteState: null, // used for change notifications
      // ------- data updating
      isDataUpdating: false,
      dataUpdating: {},
      dataUpdatingError: {},
      // ------- re-ordering
      allowReordering: true,
      isReorderMode: false,
      isReordering: false,
      drag: false,
      isRouteChangeDialogOpen: false,
      notesRouteChange: null,
      ordersThatMoved: [],
      ordersWithRouteChange: [],
      reorderingSuccess: false,
      reorderingError: null,
      // ------- sending notifications
      enableSendNotificationsOnChange: true,
      isSendingNotifications: false,
      sendingNotificationsSuccessMessage: null,
      sendingNotificationsError: null,
      // ------- responsive handling
      isMobileScreen: window.innerWidth < 768
    }
  },

  computed: {
    orders () {
      return this.$root.utils.orderBy(this.route.orders, ['estimated_receiver_arrival_time'], ['asc'])
    },
    // The proper sort for the dropoffs (per the built route_info.steps)
    sortForDropoffs () {
      const routeSteps = this.route.route_info.steps || []
      return routeSteps
        .filter(step => step.type === 'dropoff' && Array.isArray(step.order_ids))
        .reduce((acc, step) => acc.concat(step.order_ids), [])
    },
    haveDeliveriesChanged () {
      return this.ordersToDeliver.some((order, index) => order.id !== this.activeOrdersToDeliver[index].id)
    },
    canSaveRouteChanges () {
      const geoLocation = this.$refs['client-geo-location']
      const reRouteCoords = (geoLocation) ? geoLocation.getInfo() : {}
      const hasCoords = reRouteCoords.latitude && reRouteCoords.longitude
      return this.notesRouteChange && hasCoords
    },
    // Reactive function that checks if route change is required for a reschedule
    requiresRouteChangeForReschedule () {
      return (order) => {
        if (!order) return false
        const orderStatus = order.order_status
        const toOrAtLocation = orderStatus === 'TO_SHIPPER' || orderStatus === 'AT_SHIPPER' || orderStatus === 'TO_RECEIVER' || orderStatus === 'AT_RECEIVER'

        // If in-transit, check if this is the last deliverable order with this status
        // If it is, we have a route change
        if (toOrAtLocation) {
          return this.ordersToDeliver.filter(it => it.order_status === orderStatus && it.id !== order.id).length === 0
        } else {
          return false
        }
      }
    }
  },

  mounted () {
    // Initialize viewport detection
    this.handleResize = () => {
      this.isMobileScreen = window.innerWidth < 768
    }
    window.addEventListener('resize', this.handleResize)
    this.isMobileScreen = window.innerWidth < 768 // set initial value
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
  },

  watch: {
    // Filter out orders of Route for grouped display
    route: {
      handler (updated) {
        if (updated) {
          // Isolate orders that are completed
          this.ordersCompleted = this.orders.filter(it => (
            it.order_status === 'DELIVERED' ||
            it.order_status === 'RESCHEDULED' ||
            it.order_status === 'REJECTED_ON_DELIVERY' ||
            it.order_status === 'CANCELED'
          ))
          // Isolate orders to deliver
          this.ordersToDeliver = this.orders.filter(it => (
            it.order_status !== 'DELIVERED' &&
            it.order_status !== 'RESCHEDULED' &&
            it.order_status !== 'REJECTED_ON_DELIVERY' &&
            it.order_status !== 'CANCELED'
          )).filter(order => this.sortForDropoffs.includes(order.id))
            .sort((a, b) => this.sortForDropoffs.indexOf(a.id) - this.sortForDropoffs.indexOf(b.id))

          // Map a copy for revert and compare
          this.activeOrdersToDeliver = [...this.ordersToDeliver]

          // Build orders that can move
          this.ordersThatCanMove = this.ordersToDeliver.filter(it => this.canMoveInReorder(it))

          // Active delivery index is where the first order to deliver
          this.activeDeliveryStepIndex = (this.ordersToDeliver.length > 0)
            ? this.route.route_info.steps.findIndex(it => it.type === 'dropoff' && it.order_ids.includes(this.ordersToDeliver[0].id))
            : -1
        }
      },
      deep: true,
      immediate: true
    }
  },

  methods: {
    // Emit close action
    closeOrderManager () {
      if (this.isReorderMode) this.cancelReorderChanges()
      this.dataUpdatingError = {}
      this.reorderingError = null
      this.sendingNotificationsError = null
      this.reorderingSuccess = false
      this.sendingNotificationsSuccessMessage = null
      this.$emit('close')
    },

    openRouteChangeDialog () {
      this.isRouteChangeDialogOpen = true
    },

    closeRouteChangeDialog (cancel) {
      this.isRouteChangeDialogOpen = false
      if (cancel && this.isReorderMode) this.cancelReorderChanges()
    },

    formatDate (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'dddd MMM D, YYYY') : ''
    },

    enableReorderMode () {
      this.previousRouteState = this.buildPreviousRouteState() // save estimated times for change notifications
      this.isReorderMode = true
    },

    cancelReorderChanges () {
      this.isReorderMode = false
      this.notesRouteChange = null
      this.ordersToDeliver = [...this.activeOrdersToDeliver] // revert any changes
      this.ordersThatMoved = []
      this.ordersWithRouteChange = []
      this.previousRouteState = null // clear route state
    },

    canMoveInReorder (order = {}) {
      return order.order_status === 'SCHEDULED' ||
        order.order_status === 'TO_SHIPPER' ||
        order.order_status === 'AT_SHIPPER' ||
        order.order_status === 'PICKED_UP' ||
        order.order_status === 'TO_RECEIVER' ||
        order.order_status === 'AT_RECEIVER'
    },

    handleDragStart () {
      this.drag = true
    },

    // Handle order move (re-ordering)
    handleMove (event) {
      this.drag = false // reset drag state

      // Emit the moved order
      const movedOrder = this.ordersToDeliver[event.newIndex]
      this.$emit('orderMoved', movedOrder)
    },

    // Clear re-ordering error and revert changes
    clearReorderingError () {
      this.reorderingError = null
      this.cancelReorderChanges()
    },

    // Clear re-ordering success message
    clearReorderingSuccess () {
      this.reorderingSuccess = false
    },

    // Clear sending notifications error
    clearSendingNotificationsError () {
      this.sendingNotificationsError = null
    },

    // -------------------------------------------------------------------------
    // Builds the sorted locations payload used for re-ordering.
    // -------------------------------------------------------------------------
    // NOTE - This logic does not support staggered pickups !!!
    // (i.e. pickups after dropoffs where multiple pickup locations exist on the route)
    //
    // But, we are currenlty locking all pickups to happen first. So it is not
    // a problem at the moment - |M|
    // -------------------------------------------------------------------------
    buildSortedLocations () {
      // Add all pickup locations
      const pickupLocations = this.orders.map(order => ({
        type: 'pickup-location',
        loc: order.shipper,
        order
      }))

      // Add completed dropoffs
      const completedDropoffs = this.ordersCompleted.map(order => ({
        type: 'dropoff-location',
        loc: order.receiver,
        order
      }))

      // Add remaining dropoffs
      const remainingDropoffs = this.ordersToDeliver.map(order => ({
        type: 'dropoff-location',
        loc: order.receiver,
        order
      }))

      return [...pickupLocations, ...completedDropoffs, ...remainingDropoffs]
    },

    // Builds the previous state payload for change notifications and for comparing re-order updates
    buildPreviousRouteState () {
      const previousState = {}
      previousState.orders = this.activeOrdersToDeliver.reduce((acc, order) => {
        acc[order.id] = {
          pickup_est_arrival: order.estimated_shipper_arrival_time,
          dropoff_est_arrival: order.estimated_receiver_arrival_time,
          step_index: this.activeOrdersToDeliver.findIndex(it => it.id === order.id)
        }
        return acc
      }, {})

      return previousState
    },

    // Processes the requested re-ordering of dropoffs to determine if the driver needs
    // to provide route change information beforehand.
    async validateSaveReorderChanges () {
      // Determine orders that have moved
      this.ordersThatMoved = this.ordersToDeliver.filter((order, currentIndex) => {
        const previousState = this.previousRouteState.orders[order.id]
        return previousState && previousState.step_index !== currentIndex
      })

      // Determine orders that are changing their route
      this.ordersWithRouteChange = this.ordersThatMoved.filter((order) => {
        // Only consider orders that are TO_RECEIVER or AT_RECEIVER
        if (order.order_status !== 'TO_RECEIVER' && order.order_status !== 'AT_RECEIVER') {
          return false
        }

        // Find the step this order belongs to
        const currentStep = this.route.route_info.steps.find((step) => {
          return step.type === 'dropoff' && step.order_ids.includes(order.id)
        })

        if (!currentStep) {
          return false
        }

        // Get the current index of this order
        const currentIndex = this.ordersToDeliver.findIndex(o => o.id === order.id)

        // Check if there are any orders in front of this one that are not TO_RECEIVER or AT_RECEIVER.
        // If we do, we have a route change.
        return this.ordersToDeliver.some((otherOrder, index) => {
          if (index >= currentIndex) return false // Only check orders before the current order
          return otherOrder.order_status !== 'TO_RECEIVER' && otherOrder.order_status !== 'AT_RECEIVER'
        })
      })

      // Indicates an event to inspect for a route change
      const isRouteChangeEvent = this.ordersWithRouteChange.length > 0

      // console.log(`[DEVING] ordersThatMoved (${this.ordersThatMoved.length}):`, this.ordersThatMoved)
      // console.log(`[DEVING] ordersWithRouteChange (${this.ordersWithRouteChange.length}):`, this.ordersWithRouteChange)

      // Determine if a route change will occur based on the moved orders
      let readyToSave = false
      let requiresOrderUpdate = false
      if (isRouteChangeEvent) {
        const allOrdersAtStep = this.route.route_info.steps.find((it) => {
          return it.type === 'dropoff' && it.order_ids.includes(this.ordersWithRouteChange[0].id)
        })
        const areAllOrdersChanging = allOrdersAtStep && allOrdersAtStep.order_ids.every(orderId =>
          this.ordersWithRouteChange.some(order => order.id === orderId)
        )
        // console.log('[DEVING] allOrdersAtStep:', allOrdersAtStep)
        // console.log('[DEVING] All orders changing?', areAllOrdersChanging)

        // If all orders are changing at the active step, then we need to perform a route change (via dialog).
        // Otherwise, we just need to revert the relevant orders to their PICKED_UP state.
        if (areAllOrdersChanging) {
          this.openRouteChangeDialog()
        } else {
          requiresOrderUpdate = true
          readyToSave = true
        }
      } else {
        readyToSave = true
      }

      // Perform required next actions
      if (requiresOrderUpdate) {
        try {
          this.isReordering = true
          await this.resetOrdersToPickedUp()
        } catch (err) {
          this.reorderingError = err.message || err.text || err
          return // Exit and do not save
        } finally {
          this.ordersThatMoved = []
          this.ordersWithRouteChange = []
          this.isReordering = false
        }
      }

      // Execute route change
      if (readyToSave) {
        this.saveReorderChanges()
      }
    },

    // Supports saving a re-order when orders need to be altered due to re-route scenarios
    async handleReRouteAndSave () {
      const geoLocation = this.$refs['client-geo-location']
      const reRouteCoords = (geoLocation) ? geoLocation.getInfo() : null
      const orderToCheck = this.ordersWithRouteChange[0]
      const relatedStepIndex = this.route.route_info.steps.findIndex(it => it.type === 'dropoff' && it.order_ids.includes(orderToCheck.id))
      const relatedStep = relatedStepIndex !== -1 ? this.route.route_info.steps[relatedStepIndex] : {}
      console.log('Using client geolocation:', reRouteCoords)
      // console.log(`[DEVING] Using step ${relatedStepIndex}:`, relatedStep)

      this.closeRouteChangeDialog()

      // Add route change marker and notes to route
      try {
        const routeChangeData = this.route.route_info?.route_changes || []
        const routeChange = {
          loc: { coords: reRouteCoords },
          route_step_index: relatedStepIndex,
          route_changed_at: new Date().toISOString(),
          notes: this.notesRouteChange,
          order_ids: relatedStep.order_ids || [],
          order_numbers: relatedStep.order_numbers || [],
          destination_id: relatedStep.location_id,
          destination_name: relatedStep.location_name
        }
        routeChangeData.push(routeChange)

        // Save route change info to route
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `route/${this.route.id}`,
          opts: {
            bodyData: {
              route_info: {
                ...this.route.route_info,
                route_changes: routeChangeData,
                manual: true
              }
            }
          }
        })
      } catch (err) {
        this.reorderingError = err.message || err.text || err
        return // Exit and do not save !!!
      }

      // Perform required order updates
      const requiresOrderUpdates = this.ordersWithRouteChange.length > 0
      if (requiresOrderUpdates) {
        try {
          this.isReordering = true
          await this.resetOrdersToPickedUp()
        } catch (err) {
          this.reorderingError = err.message || err.text || err
          return // Exit and do not save !!!
        } finally {
          this.ordersThatMoved = []
          this.ordersWithRouteChange = []
          this.isReordering = false
        }
      }

      // Execute route change
      this.saveReorderChanges()
    },

    // Reset relevant orders back to PICKED_UP state for a later delivery attempt
    async resetOrdersToPickedUp () {
      // console.log(`[DEVING] Resetting ${this.ordersWithRouteChange.length} orders to the PICKED_UP state.`)

      const resetOrders = this.ordersWithRouteChange.map(order =>
        this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `order/${order.id}`,
          opts: {
            bodyData: {
              to_receiver_at: null,
              at_receiver_at: null,
              order_status: 'PICKED_UP'
            }
          }
        })
      )

      return Promise.all(resetOrders)
    },

    // Triggers order schedule update notifications, if relevant
    async sendOrderUpdateNotifications () {
      this.clearReorderingSuccess()
      this.isSendingNotifications = true

      try {
        // Send update notifications
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `route/${this.route.id}/send-delivery-change-notifications`,
          opts: {
            bodyData: {
              previous_route_state: this.previousRouteState
            }
          },
          to_model: false
        })
        // NOTE: Return payload contains info on which orders triggered notifications
        // console.log('[DEVING] results:', results)

        // DEVING - success/failure examples
        // await new Promise(resolve => {
        //   setTimeout(() => {
        //     resolve(true)
        //   }, 4000)
        // })
        // await new Promise((resolve, reject) => {
        //   setTimeout(() => {
        //     reject(new Error('Failed to send notifications: Network timeout'))
        //   }, 3000)
        // })

        // Show success (for 5 seconds)
        this.sendingNotificationsSuccessMessage = (this.enableSendNotificationsOnChange)
          ? 'Success!'
          : 'Emails sent!'
        setTimeout(() => {
          this.sendingNotificationsSuccessMessage = null
        }, 5000)
      } catch (err) {
        this.sendingNotificationsError = err.message || err.text || err
      } finally {
        this.isSendingNotifications = false
      }
    },

    // Save re-order changes calling route "generate" endpoint with manually set locations
    async saveReorderChanges () {
      this.isReordering = true

      try {
        // Build sorted locations with re-ordered dropoffs
        const sortedLocations = this.buildSortedLocations()

        // Re-generate updated route
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `route/${this.route.id}/generate`,
          opts: {
            bodyData: {
              scheduled_start_time: this.route.scheduled_start_time,
              is_manual_mode: true,
              locations: sortedLocations
            }
          },
          to_model: false
        })

        // DEVING - success/failure examples
        // await new Promise(resolve => {
        //   setTimeout(() => {
        //     resolve(sortedLocations)
        //   }, 3000)
        // })
        // await new Promise((resolve, reject) => {
        //   setTimeout(() => {
        //     reject(new Error('Failed to generate route: Network timeout'))
        //   }, 3000)
        // })

        // End mode and flag success
        this.reorderingSuccess = true
        this.isReorderMode = false

        if (this.enableSendNotificationsOnChange) this.sendOrderUpdateNotifications()

        // Emit re-ordered event
        this.$emit('reordered', sortedLocations)
      } catch (err) {
        this.reorderingError = err.message || err.text || err
      } finally {
        this.ordersThatMoved = []
        this.ordersWithRouteChange = []
        this.isReordering = false
      }
    },

    // Handle reschedule of order within a route
    // data.order              = the order to reschedule
    // data.notes              = reason for reschedule (optional)
    // data.notes_route_change = route change notes (optional)
    // data.client_coords      = the client coords for re-route (optional)
    async rescheduleOrder (data = {}) {
      const orderToReschedule = data.order
      const notes = data.notes
      const notesRouteChange = data.notes_route_change
      const clientCoords = data.client_coords
      if (!orderToReschedule) return false

      this.isDataUpdating = true
      this.$set(this.dataUpdating, orderToReschedule.id, true)

      try {
        const bodyData = { order_ids: [orderToReschedule.id] }
        if (notes) bodyData.dispatcher_notes = notes
        if (notesRouteChange) bodyData.route_change_notes = notesRouteChange
        if (clientCoords) bodyData.re_route_coordinates = clientCoords

        const rescheduledOrder = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `route/${this.route.id}/reschedule-orders`,
          opts: {
            bodyData
          }
        })

        // DEVING - success/failure examples
        // const rescheduledOrder = await new Promise(resolve => {
        //   setTimeout(() => {
        //     resolve({ id: orderToReschedule.id })
        //   }, 6000)
        // })
        // const rescheduledOrder = await new Promise((resolve, reject) => {
        //   setTimeout(() => {
        //     reject(new Error('Failed to reschedule order: Network timeout'))
        //   }, 6000)
        // })

        // Emit rescheduled order
        this.$emit('rescheduled', rescheduledOrder)
      } catch (err) {
        const errMessage = (this.$root.utils.isEmpty(err))
          ? 'The order could not be rescheduled.'
          : err.message || err.text || err
        this.$set(this.dataUpdatingError, orderToReschedule.id, errMessage)
        setTimeout(() => {
          this.$delete(this.dataUpdatingError, orderToReschedule.id)
        }, 9000)
      } finally {
        this.isDataUpdating = false
        this.$delete(this.dataUpdating, orderToReschedule.id)
      }
    },

    // Handle order cancel
    // data.order = the order to cancel
    // data.notes = notes (optional)
    async cancelOrder (data = {}) {
      const orderToCancel = data.order
      // const notes = data.notes // not implemented !!!
      if (!orderToCancel) return false

      this.isDataUpdating = true
      this.$set(this.dataUpdating, orderToCancel.id, true)

      try {
        const bodyData = { order_ids: [orderToCancel.id] }
        const canceledOrder = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `route/${this.route.id}/cancel-orders`,
          opts: {
            bodyData
          }
        })

        // Emit canceled order
        this.$emit('canceled', canceledOrder)
      } catch (err) {
        const errMessage = (this.$root.utils.isEmpty(err))
          ? 'The order could not be canceled.'
          : err.message || err.text || err
        this.$set(this.dataUpdatingError, orderToCancel.id, errMessage)
        setTimeout(() => {
          this.$delete(this.dataUpdatingError, orderToCancel.id)
        }, 9000)
      } finally {
        this.isDataUpdating = false
        this.$delete(this.dataUpdating, orderToCancel.id)
      }
    }
  }
}
</script>

<style>
  .delivery-order-manager .order-manager-toolbar {
    margin-top: 6px;
    margin-right: 8px;
    margin-bottom: 4px;
    margin-left: 8px;
  }

  .delivery-order-manager .order-manager-toolbar .v-toolbar__content {
    background-color: #303030 !important;
    border-radius: 4px;
  }

  .delivery-order-manager .order-manager-toolbar .toolbar-button {
    text-align: center;
  }

  .delivery-order-manager .list-empty-text {
    color: #727272;
    font-size: 1.08rem;
    font-style: italic;
  }

  .delivery-order-manager .route-info-text {
    color: #727272;
    font-size: 1.08rem;
  }

  .delivery-order-manager .updating-text {
    color: #9c9c9c;
    font-size: 1.0rem;
  }

  .delivery-order-manager .reorder-send-message-text {
    font-size: 0.9rem;
  }

  .delivery-order-manager .reordering-success-text {
    color: #4caf4f;
    font-size: 1.0rem;
  }

  .delivery-order-manager .reordering-error-text {
    /* color: #ff5252; */
    color: #ed4b4b;
  }

  .delivery-order-manager .main-content {
    position: absolute;
    top: 80px;
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch; /* For smooth scrolling on iOS */
  }

  .delivery-order-manager .order-group-container {
    max-width: 820px;
  }

  /* Route Change Dialog */
  .client-coords-dialog-text {
    font-size: 18px !important;
  }

  .client-coords-container {
    border: 1px solid #f6a626;
    border-radius: 4px;
  }

  .client-coords-text-main {
    font-size: 1.08rem;
  }

  .client-coords-text-sub {
    color: #9c9c9c;
    font-size: 0.93rem;
    font-style: italic;
  }
</style>
